import { Loader } from "@/components/ui/loaders";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Box,
  Button,
  Stack,
  Typography,
  colors,
  useTheme,
} from "@mui/material";
import React, { useRef, useState } from "react";

type Props = {
  label: string;
  loading: boolean;
  onFilesUpload: (file: File[]) => void;
  onSecondaryAction?: () => void;
  secondaryActionLabel?: string;
};

export function DragDropUpload({
  label,
  loading,
  onFilesUpload,
  onSecondaryAction,
  secondaryActionLabel,
}: Props) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFilesUpload(Array.from(e.dataTransfer.files));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      onFilesUpload(Array.from(e.target.files));
    }
  };

  const onButtonClick = () => {
    inputRef.current!.click();
  };

  return (
    <Box
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      sx={{
        height: 250,
        width: "100%",
        borderRadius: (theme) => theme.shape.borderRadius,
        border: "1px dashed",
        borderColor: dragActive ? "black" : colors.grey[400],
        bgcolor: dragActive
          ? theme.palette.action.hover
          : theme.palette.background.paper,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        transition: "border .24s ease-in-out",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          bgcolor: theme.palette.action.hover,
        },
      }}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          <input
            ref={inputRef}
            type="file"
            onChange={handleChange}
            style={{ display: "none" }}
          />

          <Stack gap={1} alignItems="center">
            <CloudUploadIcon
              sx={{
                fontSize: 60,
                color: dragActive
                  ? theme.palette.primary.main
                  : colors.grey[500],
              }}
            />

            <Typography variant="h6">{label}</Typography>
            <Typography variant="body2" color={colors.grey[500]}>
              OR
            </Typography>
            <Stack direction="row" gap={2}>
              <Button
                variant="contained"
                onClick={onButtonClick}
                sx={{ textTransform: "none" }}
              >
                Browse files
              </Button>
              {secondaryActionLabel && onSecondaryAction && (
                <Button
                  variant="outlined"
                  onClick={onSecondaryAction}
                  sx={{ textTransform: "none" }}
                >
                  {secondaryActionLabel}
                </Button>
              )}
            </Stack>
          </Stack>
        </>
      )}
    </Box>
  );
}
